import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { BrazilFlag, USAFlag, SpainFlag } from './FlagIcons';

interface LanguageSelectorProps {
  className?: string;
  showLabel?: boolean;
}

const languages = [
  { code: 'pt', name: 'Português', flag: BrazilFlag },
  { code: 'en', name: 'English', flag: USAFlag },
  { code: 'es', name: 'Español', flag: SpainFlag },
];

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({ 
  className = '', 
  showLabel = true 
}) => {
  const { language, setLanguage, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Garantir que sempre encontre o idioma correto
  const currentLanguage = languages.find(lang => lang.code === language) || 
                         languages.find(lang => lang.code === 'pt') || 
                         languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (langCode: string) => {
    setLanguage(langCode);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {showLabel && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('settings.language')}
        </label>
      )}
      
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="
          relative bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 
          rounded-lg px-2 py-2 text-left cursor-pointer focus:outline-none focus:ring-2 
          focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 
          hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200
          flex items-center gap-1 w-12 h-10
        "
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <currentLanguage.flag size={16} className="flex-shrink-0" />
        <svg
          className={`h-3 w-3 text-gray-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="
          absolute z-50 mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-300 
          dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-auto right-0
        ">
          <ul className="py-1" role="listbox">
            {languages.map((lang) => (
              <li key={lang.code}>
                <button
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`
                    w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 
                    transition-colors duration-150 flex items-center gap-2
                    ${language === lang.code 
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' 
                      : 'text-gray-900 dark:text-gray-100'
                    }
                  `}
                  role="option"
                  aria-selected={language === lang.code}
                >
                  <lang.flag size={18} className="flex-shrink-0" />
                  <span className="font-medium">{lang.name}</span>
                  {language === lang.code && (
                    <svg
                      className="ml-auto h-4 w-4 text-blue-600 dark:text-blue-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};